import React, { useEffect, useMemo, useState } from "react";

import { ToastMain, SweetAlertShow, LoadingDiv } from "../master/Master";

import { getBatchList } from "../../api/BatchService";
import { getSessions } from "../../api/SessionsService";
import { StaffList } from "../../api/StudentsAttendance";
import { subjectList } from "../../api/Subjects";

import Header from "../master/SideBar";
import Nav from "../master/NavBar";
import { workReportList, workReportTrackingDashboard } from "../../api/Staff";
import Pagination from "../components/Pagination";
import AcademicHeader from "./AcademicHeader";

const getTodayDate = () => {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};

const TODAY = getTodayDate();

const TABS = {
  OVERVIEW: "overview",
  STAFF: "staff",
  BATCH: "batch",
  SUBJECT: "subject",
  ALL: "all",
};

export default function StaffWorkReports() {
  // ---------- shared master data ----------
  const [sessions, setSessions] = useState([]);
  const [batches, setBatches] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [staffs, setStaffs] = useState([]);

  const [selectedSession, setSelectedSession] = useState("");
  const [selectedStaff, setSelectedStaff] = useState("");
  const [selectedBatch, setSelectedBatch] = useState("");
  const [selectedSubject, setSelectedSubject] = useState("");

  const [fromDate, setFromDate] = useState(TODAY);
  const [toDate, setToDate] = useState(TODAY);

  // date used by the tracking dashboard (Overview / Staff-wise / Batch-wise / Subject-wise)
  const [trackingDate, setTrackingDate] = useState(TODAY);

  const [loading, setLoading] = useState(false);
  const [dashboardLoading, setDashboardLoading] = useState(false);

  const [activeTab, setActiveTab] = useState(TABS.OVERVIEW);

  // ---------- existing "All Reports" list state (unchanged behaviour) ----------
  const [reports, setReports] = useState([]);
  const [filters, setFilters] = useState({ page: 1 });
  const [navi, setNavi] = useState({});
  const [previewItem, setPreviewItem] = useState(null);

  // ---------- new tracking dashboard state ----------
  const [dashboard, setDashboard] = useState(null); // { summary, staff_wise, batch_wise, subject_wise }
  const [summaryFilter, setSummaryFilter] = useState(""); // "submitted" | "not_submitted" | ""

  // ================= master data loaders =================

  const getSessionList = async () => {
    try {
      const response = await getSessions();

      if (response?.data?.status) {
        const sessionData =
          response?.data?.data?.data || response?.data?.data || [];

        const sessionList = Array.isArray(sessionData) ? sessionData : [];
        setSessions(sessionList);

        const activeSession = sessionList.find(
          (session) => String(session?.status) === "1",
        );

        if (activeSession?.id) {
          setSelectedSession(String(activeSession.id));
        }
      } else {
        setSessions([]);
      }
    } catch (error) {
      setSessions([]);
    }
  };

  const fetchBatches = async (sessionId) => {
    if (!sessionId) {
      setBatches([]);
      return;
    }

    try {
      const response = await getBatchList(`&status=1&session_id=${sessionId}`);

      if (response?.data?.status) {
        const batchData = response?.data?.batches || response?.data?.data || [];
        setBatches(Array.isArray(batchData) ? batchData : []);
      } else {
        setBatches([]);
      }
    } catch (error) {
      setBatches([]);
    }
  };

  const getSubjects = async () => {
    try {
      const response = await subjectList();

      if (response?.data?.status) {
        setSubjects(
          Array.isArray(response?.data?.data) ? response.data.data : [],
        );
      } else {
        setSubjects([]);
      }
    } catch (error) {
      setSubjects([]);
    }
  };

  const getStaffs = async () => {
    try {
      const response = await StaffList("&type=Teaching");

      if (response?.data?.status) {
        const staffData =
          response?.data?.data?.data || response?.data?.data || [];
        setStaffs(Array.isArray(staffData) ? staffData : []);
      } else {
        setStaffs([]);
      }
    } catch (error) {
      setStaffs([]);
    }
  };

  useEffect(() => {
    getSessionList();
    getSubjects();
    getStaffs();
  }, []);

  useEffect(() => {
    fetchBatches(selectedSession);
  }, [selectedSession]);

  const handleSessionChange = (e) => {
    const sessionId = e.target.value;
    setSelectedSession(sessionId);
    setSelectedBatch("");
    setFilters((prev) => ({ ...prev, page: 1 }));
  };

  // ================= existing "All Reports" list (unchanged) =================

  const fetchReports = async () => {
    setLoading(true);

    try {
      let addUrl = "";

      if (selectedSession) addUrl += `&session_id=${selectedSession}`;
      if (selectedStaff) addUrl += `&staff_id=${selectedStaff}`;
      if (selectedBatch) addUrl += `&batch_id=${selectedBatch}`;
      if (selectedSubject) addUrl += `&subject_id=${selectedSubject}`;
      if (fromDate) addUrl += `&from_date=${fromDate}`;
      if (toDate) addUrl += `&to_date=${toDate}`;

      addUrl += `&page=${filters.page}`;

      const response = await workReportList(addUrl);

      if (response?.data?.status) {
        const list = response?.data?.data?.data || [];
        setReports(Array.isArray(list) ? list : []);
        setNavi(response?.data?.data || {});
      } else {
        setReports([]);
        setNavi({});
        ToastMain.fire({
          icon: "info",
          title: response?.data?.message || "Unable to fetch work reports.",
        });
      }
    } catch (error) {
      setReports([]);
      setNavi({});
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
          error?.response?.message ??
          error?.message ??
          "Something went wrong.",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === TABS.ALL) {
      fetchReports();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeTab,
    selectedSession,
    selectedStaff,
    selectedBatch,
    selectedSubject,
    fromDate,
    toDate,
    filters.page,
  ]);

  // ================= new tracking dashboard =================

  const fetchDashboard = async () => {
    setDashboardLoading(true);

    try {
      let addUrl = `&date=${trackingDate}`;
      if (selectedSession) addUrl += `&session_id=${selectedSession}`;

      const response = await workReportTrackingDashboard(addUrl);

      if (response?.data?.status) {
        setDashboard(response?.data?.data || null);
      } else {
        setDashboard(null);
        ToastMain.fire({
          icon: "info",
          title: response?.data?.message || "Unable to fetch tracking data.",
        });
      }
    } catch (error) {
      setDashboard(null);
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
          error?.response?.message ??
          error?.message ??
          "Something went wrong.",
      );
    } finally {
      setDashboardLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab !== TABS.ALL) {
      fetchDashboard();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, trackingDate, selectedSession]);

  const handleReset = () => {
    const activeSession = sessions.find(
      (session) => String(session?.status) === "1",
    );

    setSelectedSession(activeSession?.id ? String(activeSession.id) : "");
    setSelectedStaff("");
    setSelectedBatch("");
    setSelectedSubject("");
    setFromDate(TODAY);
    setToDate(TODAY);
    setTrackingDate(TODAY);
    setSummaryFilter("");
    setFilters((prev) => ({ ...prev, page: 1 }));
  };

  const hasActiveFilters = Boolean(
    selectedStaff ||
    selectedBatch ||
    selectedSubject ||
    fromDate !== TODAY ||
    toDate !== TODAY,
  );

  const truncate = (text, length = 60) => {
    if (!text) return "-";
    return text.length > length ? `${text.slice(0, length)}...` : text;
  };

  // ---- derived: filtered staff-wise rows for the Staff tab, honouring summary card filter + staff dropdown ----
  const staffWiseRows = useMemo(() => {
    const rows = dashboard?.staff_wise || [];
    let filtered = rows;

    if (summaryFilter === "submitted") {
      filtered = filtered.filter((r) => r.submitted);
    } else if (summaryFilter === "not_submitted") {
      filtered = filtered.filter((r) => !r.submitted);
    }

    if (selectedStaff) {
      filtered = filtered.filter(
        (r) => String(r.staff_id) === String(selectedStaff),
      );
    }

    return filtered;
  }, [dashboard, summaryFilter, selectedStaff]);

  const summary = dashboard?.summary || {};

  return (
    <>
      {(loading || dashboardLoading) && <LoadingDiv />}

      <div className="layout-wrapper layout-content-navbar">
        <div className="layout-container">
          <Header />

          <div className="layout-page">
            <Nav />

            <div className="content-wrapper">
              <div className="container-fluid">
                <div className="row mt-4">
                  <AcademicHeader />

                  <div className="col-md-12">
                    <div className="card shadow-sm border-0">
                      {/* ===== Header ===== */}
                      <div className="card-header d-flex flex-wrap align-items-center justify-content-between gap-2">
                        <div className="d-flex align-items-center gap-3">
                          <div
                            className="rounded-3 bg-label-primary d-flex align-items-center justify-content-center"
                            style={{ width: 44, height: 44 }}
                          >
                            <i className="ri-file-list-3-line fs-4" />
                          </div>
                          <div>
                            <h4 className="text-primary m-0">
                              Staff Work Report Tracking
                            </h4>
                            <small className="text-muted">
                              Who taught what, and who hasn't submitted yet
                            </small>
                          </div>
                        </div>

                        {activeTab !== TABS.ALL && (
                          <div
                            className="form-floating form-floating-outline"
                            style={{ maxWidth: 200 }}
                          >
                            <input
                              type="date"
                              className="form-control"
                              value={trackingDate}
                              onChange={(e) => setTrackingDate(e.target.value)}
                            />
                            <label>Date</label>
                          </div>
                        )}
                      </div>

                      {/* ===== Summary cards ===== */}
                      {activeTab !== TABS.ALL && (
                        <div className="card-body pb-2">
                          <div className="row g-2">
                            <SummaryCard
                              label="Total Staff"
                              value={summary.total_staff}
                              icon="ri-team-line"
                              color="secondary"
                              active={summaryFilter === ""}
                              onClick={() => setSummaryFilter("")}
                            />
                            <SummaryCard
                              label="Submitted"
                              value={summary.submitted}
                              icon="ri-checkbox-circle-line"
                              color="success"
                              active={summaryFilter === "submitted"}
                              onClick={() => {
                                setSummaryFilter("submitted");
                                setActiveTab(TABS.STAFF);
                              }}
                            />
                            <SummaryCard
                              label="Not Submitted"
                              value={summary.not_submitted}
                              icon="ri-close-circle-line"
                              color="danger"
                              active={summaryFilter === "not_submitted"}
                              onClick={() => {
                                setSummaryFilter("not_submitted");
                                setActiveTab(TABS.STAFF);
                              }}
                            />
                            <SummaryCard
                              label="Total Reports"
                              value={summary.total_reports}
                              icon="ri-file-list-3-line"
                              color="primary"
                            />
                            <SummaryCard
                              label="Batches Covered"
                              value={summary.batches_covered}
                              icon="ri-group-line"
                              color="info"
                              onClick={() => setActiveTab(TABS.BATCH)}
                            />
                            <SummaryCard
                              label="Subjects Covered"
                              value={summary.subjects_covered}
                              icon="ri-book-open-line"
                              color="warning"
                              onClick={() => setActiveTab(TABS.SUBJECT)}
                            />
                          </div>
                        </div>
                      )}

                      {/* ===== Tabs ===== */}
                      <div className="card-body pt-0 pb-2">
                        <ul className="nav nav-pills gap-2 flex-wrap">
                          <TabButton
                            label="Staff-wise"
                            icon="ri-user-line"
                            active={activeTab === TABS.STAFF}
                            onClick={() => setActiveTab(TABS.STAFF)}
                          />
                          <TabButton
                            label="Batch-wise"
                            icon="ri-group-line"
                            active={activeTab === TABS.BATCH}
                            onClick={() => setActiveTab(TABS.BATCH)}
                          />
                          <TabButton
                            label="Subject-wise"
                            icon="ri-book-open-line"
                            active={activeTab === TABS.SUBJECT}
                            onClick={() => setActiveTab(TABS.SUBJECT)}
                          />
                          <TabButton
                            label="All Reports"
                            icon="ri-list-check-2"
                            active={activeTab === TABS.ALL}
                            onClick={() => setActiveTab(TABS.ALL)}
                          />
                        </ul>
                      </div>

                      {/* ===== STAFF-WISE TAB ===== */}
                      {activeTab === TABS.STAFF && (
                        <div className="card-body pt-0">
                          {summaryFilter && (
                            <div className="mb-2">
                              <span className="badge bg-label-primary">
                                Showing:{" "}
                                {summaryFilter === "submitted"
                                  ? "Submitted"
                                  : "Not Submitted"}
                                <button
                                  type="button"
                                  className="btn-close btn-close-sm ms-2"
                                  style={{ fontSize: 10 }}
                                  onClick={() => setSummaryFilter("")}
                                />
                              </span>
                            </div>
                          )}

                          {staffWiseRows.length === 0 ? (
                            <EmptyState text="No teaching staff found for this filter." />
                          ) : (
                            <div className="accordion" id="staffWiseAccordion">
                              {staffWiseRows.map((row) => (
                                <div
                                  className="accordion-item"
                                  key={row.staff_id}
                                >
                                  <h2 className="accordion-header">
                                    <button
                                      className="accordion-button collapsed d-flex justify-content-between"
                                      type="button"
                                      data-bs-toggle="collapse"
                                      data-bs-target={`#staff-${row.staff_id}`}
                                    >
                                      <span className="d-flex align-items-center gap-2">
                                        <i className="ri-user-3-line" />
                                        <strong>{row.staff_name}</strong>
                                        {row.designation && (
                                          <span className="text-muted small">
                                            ({row.designation})
                                          </span>
                                        )}
                                      </span>
                                      <span
                                        className={`badge ${
                                          row.submitted
                                            ? "bg-success"
                                            : "bg-danger"
                                        } me-3 ms-2`}
                                      >
                                        {row.submitted
                                          ? `Submitted (${row.reports_count})`
                                          : "Not Submitted"}
                                      </span>
                                    </button>
                                  </h2>
                                  <div
                                    id={`staff-${row.staff_id}`}
                                    className="accordion-collapse collapse"
                                    data-bs-parent="#staffWiseAccordion"
                                  >
                                    <div className="accordion-body">
                                      {row.reports.length === 0 ? (
                                        <p className="text-muted small mb-0">
                                          No work report submitted for this
                                          date.
                                        </p>
                                      ) : (
                                        <div className="table-responsive">
                                          <table className="table table-sm align-middle mb-0">
                                            <thead>
                                              <tr>
                                                <th>Batch</th>
                                                <th>Subject</th>
                                                <th>Topic Covered</th>
                                                <th>Work Description</th>
                                              </tr>
                                            </thead>
                                            <tbody>
                                              {row.reports.map((r) => (
                                                <tr key={r.id}>
                                                  <td>{r.batch || "-"}</td>
                                                  <td>{r.subject || "-"}</td>
                                                  <td className="text-primary fw-semibold">
                                                    {r.topic_covered || "-"}
                                                  </td>
                                                  <td
                                                    role={
                                                      r.work_description
                                                        ? "button"
                                                        : undefined
                                                    }
                                                    onClick={() =>
                                                      r.work_description &&
                                                      setPreviewItem(r)
                                                    }
                                                  >
                                                    {truncate(
                                                      r.work_description,
                                                    )}
                                                  </td>
                                                </tr>
                                              ))}
                                            </tbody>
                                          </table>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}

                      {/* ===== BATCH-WISE TAB ===== */}
                      {activeTab === TABS.BATCH && (
                        <div className="card-body pt-0">
                          {(dashboard?.batch_wise || []).length === 0 ? (
                            <EmptyState text="No work reported for any batch on this date." />
                          ) : (
                            <div className="row g-3">
                              {dashboard.batch_wise.map((b) => (
                                <div className="col-md-6" key={b.batch}>
                                  <div className="card border h-100">
                                    <div className="card-header d-flex justify-content-between align-items-center py-2">
                                      <strong>
                                        <i className="ri-group-line me-1" />
                                        {b.batch}
                                      </strong>
                                      <span className="badge bg-label-primary">
                                        {b.staff_count} staff
                                      </span>
                                    </div>
                                    <div className="card-body py-2">
                                      <ul className="list-unstyled mb-0">
                                        {b.entries.map((e, i) => (
                                          <li
                                            key={i}
                                            className={
                                              i > 0
                                                ? "pt-2 mt-2 border-top"
                                                : ""
                                            }
                                          >
                                            <div className="fw-semibold">
                                              {e.staff}
                                            </div>
                                            <div className="small text-muted">
                                              {e.subject || "-"} ·{" "}
                                              <span className="text-primary">
                                                {e.topic_covered || "-"}
                                              </span>
                                            </div>
                                          </li>
                                        ))}
                                      </ul>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}

                      {/* ===== SUBJECT-WISE TAB ===== */}
                      {activeTab === TABS.SUBJECT && (
                        <div className="card-body pt-0">
                          {(dashboard?.subject_wise || []).length === 0 ? (
                            <EmptyState text="No work reported for any subject on this date." />
                          ) : (
                            <div className="row g-3">
                              {dashboard.subject_wise.map((s) => (
                                <div className="col-md-6" key={s.subject}>
                                  <div className="card border h-100">
                                    <div className="card-header d-flex justify-content-between align-items-center py-2">
                                      <strong>
                                        <i className="ri-book-open-line me-1" />
                                        {s.subject}
                                      </strong>
                                      <span className="badge bg-label-primary">
                                        {s.staff_count} staff
                                      </span>
                                    </div>
                                    <div className="card-body py-2">
                                      <ul className="list-unstyled mb-0">
                                        {s.entries.map((e, i) => (
                                          <li
                                            key={i}
                                            className={
                                              i > 0
                                                ? "pt-2 mt-2 border-top"
                                                : ""
                                            }
                                          >
                                            <div className="fw-semibold">
                                              {e.staff}
                                            </div>
                                            <div className="small text-muted">
                                              {e.batch || "-"} ·{" "}
                                              <span className="text-primary">
                                                {e.topic_covered || "-"}
                                              </span>
                                            </div>
                                          </li>
                                        ))}
                                      </ul>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}

                      {/* ===== ALL REPORTS TAB (existing functionality, unchanged) ===== */}
                      {activeTab === TABS.ALL && (
                        <>
                          <div className="card-body pb-2">
                            <div className="d-flex align-items-center justify-content-between mb-2">
                              <span className="text-uppercase text-muted small fw-semibold">
                                <i className="ri-filter-3-line me-1" />
                                Filters
                              </span>

                              {hasActiveFilters && (
                                <button
                                  type="button"
                                  className="btn btn-sm btn-text-secondary text-decoration-underline"
                                  onClick={handleReset}
                                >
                                  Reset filters
                                </button>
                              )}
                            </div>

                            <div className="row g-2">
                              <div className="col-md-2">
                                <div className="form-floating form-floating-outline">
                                  <select
                                    className="form-select"
                                    value={selectedSession}
                                    onChange={handleSessionChange}
                                  >
                                    <option value="">All Sessions</option>
                                    {sessions?.map((session) => (
                                      <option
                                        key={session?.id}
                                        value={session?.id}
                                      >
                                        {session?.session || "Session"}
                                        {String(session?.status) === "1"
                                          ? " (Active)"
                                          : " (Inactive)"}
                                      </option>
                                    ))}
                                  </select>
                                  <label>Session</label>
                                </div>
                              </div>

                              <div className="col-md-2">
                                <div className="form-floating form-floating-outline">
                                  <select
                                    className="form-select"
                                    value={selectedStaff}
                                    onChange={(e) => {
                                      setSelectedStaff(e.target.value);
                                      setFilters((prev) => ({
                                        ...prev,
                                        page: 1,
                                      }));
                                    }}
                                  >
                                    <option value="">All Staff</option>
                                    {staffs?.map((staff) => (
                                      <option key={staff?.id} value={staff?.id}>
                                        {staff?.name}
                                      </option>
                                    ))}
                                  </select>
                                  <label>Staff</label>
                                </div>
                              </div>

                              <div className="col-md-2">
                                <div className="form-floating form-floating-outline">
                                  <select
                                    className="form-select"
                                    value={selectedBatch}
                                    onChange={(e) => {
                                      setSelectedBatch(e.target.value);
                                      setFilters((prev) => ({
                                        ...prev,
                                        page: 1,
                                      }));
                                    }}
                                    disabled={!selectedSession}
                                  >
                                    <option value="">All Batches</option>
                                    {batches?.map((batch) => (
                                      <option key={batch?.id} value={batch?.id}>
                                        {batch?.title}
                                      </option>
                                    ))}
                                  </select>
                                  <label>Batch</label>
                                </div>
                              </div>

                              <div className="col-md-2">
                                <div className="form-floating form-floating-outline">
                                  <select
                                    className="form-select"
                                    value={selectedSubject}
                                    onChange={(e) => {
                                      setSelectedSubject(e.target.value);
                                      setFilters((prev) => ({
                                        ...prev,
                                        page: 1,
                                      }));
                                    }}
                                  >
                                    <option value="">All Subjects</option>
                                    {subjects?.map((subject) => (
                                      <option
                                        key={subject?.id}
                                        value={subject?.id}
                                      >
                                        {subject?.name}
                                      </option>
                                    ))}
                                  </select>
                                  <label>Subject</label>
                                </div>
                              </div>

                              <div className="col-md-2">
                                <div className="form-floating form-floating-outline">
                                  <input
                                    type="date"
                                    className="form-control"
                                    value={fromDate}
                                    onChange={(e) => {
                                      setFromDate(e.target.value);
                                      setFilters((prev) => ({
                                        ...prev,
                                        page: 1,
                                      }));
                                    }}
                                  />
                                  <label>From Date</label>
                                </div>
                              </div>

                              <div className="col-md-2">
                                <div className="form-floating form-floating-outline">
                                  <input
                                    type="date"
                                    className="form-control"
                                    value={toDate}
                                    min={fromDate || undefined}
                                    onChange={(e) => {
                                      setToDate(e.target.value);
                                      setFilters((prev) => ({
                                        ...prev,
                                        page: 1,
                                      }));
                                    }}
                                  />
                                  <label>To Date</label>
                                </div>
                              </div>

                              <div className="col-md-2 d-flex align-items-center">
                                <button
                                  type="button"
                                  className="btn btn-outline-secondary w-100"
                                  onClick={handleReset}
                                >
                                  <i className="ri-refresh-line me-1" />
                                  Reset
                                </button>
                              </div>
                            </div>
                          </div>

                          <div className="card-body pt-2">
                            <div className="table-responsive">
                              <table className="table table-hover align-middle">
                                <thead>
                                  <tr>
                                    <th style={{ width: 48 }}>#</th>
                                    <th>Date</th>
                                    <th>Day</th>
                                    <th>Staff</th>
                                    <th>Batch</th>
                                    <th>Subject</th>
                                    <th>Topic Covered</th>
                                    <th>Work Description</th>
                                  </tr>
                                </thead>

                                <tbody>
                                  {reports.length === 0 ? (
                                    <tr>
                                      <td colSpan="9" className="border-0">
                                        <EmptyState
                                          text={
                                            hasActiveFilters
                                              ? "No entries match your current filters. Try resetting them."
                                              : "No work reports submitted for today yet."
                                          }
                                          onReset={
                                            hasActiveFilters
                                              ? handleReset
                                              : null
                                          }
                                        />
                                      </td>
                                    </tr>
                                  ) : (
                                    reports.map((item, index) => (
                                      <tr key={item?.id}>
                                        <td className="text-muted">
                                          {navi?.from
                                            ? navi.from + index
                                            : index + 1}
                                        </td>
                                        <td>{item?.report_date || "-"}</td>
                                        <td>{item?.day || "-"}</td>
                                        <td>{item?.staff?.name || "-"}</td>
                                        <td>{item?.batch?.title || "-"}</td>
                                        <td>{item?.subject?.name || "-"}</td>
                                        <td className="fw-semibold text-primary">
                                          {item?.topic_covered || "-"}
                                        </td>
                                        <td
                                          role={
                                            item?.work_description
                                              ? "button"
                                              : undefined
                                          }
                                          onClick={() =>
                                            item?.work_description &&
                                            setPreviewItem(item)
                                          }
                                          title={
                                            item?.work_description
                                              ? "Click to view full description"
                                              : undefined
                                          }
                                        >
                                          {truncate(item?.work_description)}
                                        </td>
                                      </tr>
                                    ))
                                  )}
                                </tbody>
                              </table>

                              {reports.length > 0 && (
                                <Pagination
                                  data={navi}
                                  filters={filters}
                                  setFilters={setFilters}
                                />
                              )}
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {previewItem && (
        <div
          className="modal fade show d-block"
          tabIndex={-1}
          style={{ background: "rgba(0,0,0,0.5)" }}
          onClick={() => setPreviewItem(null)}
        >
          <div
            className="modal-dialog modal-dialog-centered"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content">
              <div className="modal-header bg-label-primary py-3">
                <h1 className="modal-title fs-5 d-flex align-items-center gap-2">
                  <i className="ri-file-text-line" />
                  Work Description
                </h1>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setPreviewItem(null)}
                />
              </div>

              <div className="modal-body">
                <div className="small text-muted mb-2">
                  {previewItem?.report_date} · {previewItem?.day} ·{" "}
                  {previewItem?.staff?.name || "-"}
                </div>
                <p className="mb-0" style={{ whiteSpace: "pre-wrap" }}>
                  {previewItem?.work_description}
                </p>
              </div>

              <div className="modal-footer bg-label-primary py-3">
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  onClick={() => setPreviewItem(null)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ============ small presentational helpers ============

function SummaryCard({ label, value, icon, color, active, onClick }) {
  return (
    <div className="col-6 col-md-2">
      <div
        role={onClick ? "button" : undefined}
        onClick={onClick}
        className={`card border h-100 ${active ? `border-${color} bg-label-${color}` : ""}`}
        style={{ cursor: onClick ? "pointer" : "default" }}
      >
        <div className="card-body py-3 text-center">
          <i className={`${icon} fs-3 text-${color} mb-1 d-block`} />
          <div className="fs-4 fw-bold">{value ?? 0}</div>
          <div className="small text-muted">{label}</div>
        </div>
      </div>
    </div>
  );
}

function TabButton({ label, icon, active, onClick }) {
  return (
    <li className="nav-item">
      <button
        type="button"
        className={`btn btn-sm ${active ? "btn-primary" : "btn-outline-secondary"}`}
        onClick={onClick}
      >
        <i className={`${icon} me-1`} />
        {label}
      </button>
    </li>
  );
}

function EmptyState({ text, onReset }) {
  return (
    <div className="d-flex flex-column align-items-center justify-content-center text-center py-5">
      <div
        className="rounded-circle bg-label-secondary d-flex align-items-center justify-content-center mb-3"
        style={{ width: 64, height: 64 }}
      >
        <i className="ri-file-list-3-line fs-2 text-muted" />
      </div>
      <h6 className="mb-1">No records found</h6>
      <p className="text-muted small mb-3" style={{ maxWidth: 340 }}>
        {text}
      </p>
      {onReset && (
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary"
          onClick={onReset}
        >
          Reset filters
        </button>
      )}
    </div>
  );
}
